import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Phone, Clock, AlertTriangle, CheckCircle, Calendar, User, Trash2 } from 'lucide-react';
import api from '../../services/api';

function NumerosNaoAtendidos() {
  const [numeros, setNumeros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage] = useState(20);
  const [tentando, setTentando] = useState({});

  useEffect(() => {
    carregarNumeros();
  }, [page, searchTerm]);

  const carregarNumeros = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        ...(searchTerm && { busca: searchTerm })
      });

      const response = await api.get(`/sdr-voz/tentativas/pendentes?${params}`);
      setNumeros(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Erro ao carregar números:', err);
    } finally {
      setLoading(false);
    }
  };

  const tentarNovamente = async (numero) => {
    setTentando(prev => ({ ...prev, [numero]: true }));
    try {
      await api.post('/sdr-voz/tentativas/iniciar', { numero });
      // Remover da lista após iniciar
      setNumeros(prev => prev.filter(n => n.numero !== numero));
      setTotal(prev => prev - 1);
    } catch (err) {
      console.error('Erro ao tentar novamente:', err);
    } finally {
      setTentando(prev => ({ ...prev, [numero]: false }));
    }
  };

  const adicionarBlacklist = async (numero, nome) => {
    if (!window.confirm(`Adicionar ${numero} à blacklist?`)) return;

    try {
      await api.post('/sdr-voz/blacklist', {
        numero,
        motivo: 'Cliente não interessado',
        origem: 'sdr'
      });
      // Remover da lista
      setNumeros(prev => prev.filter(n => n.numero !== numero));
      setTotal(prev => prev - 1);
    } catch (err) {
      console.error('Erro ao adicionar à blacklist:', err);
    }
  };

  const getMotivoColor = (motivo) => {
    switch (motivo) {
      case 'caixa_postal':
        return 'bg-yellow-100 text-yellow-800';
      case 'ocupado':
        return 'bg-orange-100 text-orange-800';
      case 'nao_atendeu':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatarTentativa = (tentativa, max) => {
    return `${tentativa}/${max}`;
  };

  const formatarProximaTentativa = (data) => {
    if (!data) return '-';
    const agora = new Date();
    const proxima = new Date(data);
    const diff = proxima - agora;

    if (diff < 0) return 'Aguardando';

    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (horas > 0) {
      return `Em ${horas}h ${minutos}m`;
    }
    return `Em ${minutos}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Números Não Atendidos</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Números para tentativa de nova ligação (máximo 5 tentativas)
          </p>
        </div>
        <button onClick={carregarNumeros} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
          <RefreshCw size={16} /> Atualizar
        </button>
      </div>

      {/* Filtro */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por número ou nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {/* Lista de Números */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-700 text-sm">
              Números para tentativa ({numeros.length}/{total})
            </h2>
            <span className="text-xs text-gray-500">
              Página {page} de {Math.ceil(total / itemsPerPage)}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : numeros.length === 0 ? (
          <div className="py-12 text-center text-gray-300">
            <Phone size={48} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhum número pendente de tentativa</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {numeros.map(numero => (
              <div key={numero.numero} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <AlertTriangle size={20} className="text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{numero.nome}</p>
                      <p className="text-xs text-gray-500 font-mono">{numero.numero}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500">{numero.produto}</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Tenta {formatarTentativa(numero.tentativa_atual, numero.max_tentativas)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMotivoColor(numero.motivo)}`}>
                        {numero.motivo}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatarProximaTentativa(numero.proxima_tentativa)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3">
                  <button
                    onClick={() => adicionarBlacklist(numero.numero, numero.nome)}
                    className="flex items-center gap-1 px-3 py-1.5 border border-red-200 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14} /> Blacklist
                  </button>
                  <button
                    onClick={() => tentarNovamente(numero.numero)}
                    disabled={tentando[numero.numero]}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-secondary disabled:opacity-50 transition-colors"
                  >
                    {tentando[numero.numero] ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Ligando...
                      </>
                    ) : (
                      <>
                        <Phone size={14} /> Tentar Novamente
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginação */}
        {total > itemsPerPage && (
          <div className="px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">
                Página {page} de {Math.ceil(total / itemsPerPage)}
              </span>
              <button
                onClick={() => setPage(Math.min(Math.ceil(total / itemsPerPage), page + 1))}
                disabled={page === Math.ceil(total / itemsPerPage)}
                className="px-3 py-1 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NumerosNaoAtendidos;